/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-floating-promises, react-hooks/exhaustive-deps, @typescript-eslint/no-unsafe-argument, @typescript-eslint/strict-boolean-expressions */

import { useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { Box, Drawer, Fab, IconButton, Tooltip } from '@mui/material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import useIsMobile from 'hooks/useIsMobile';
import useFoundersNotes from 'hooks/foundersNotes/useFoundersNotes';
import { Theme } from '@mui/material/styles';
import { FoundersNote } from 'components/foundersNotes/FoundersNote';
import { FoundersNoteData, FoundersNotesIdMap } from 'types/foundersNotes';
import { parseNoteJson } from 'util/foundersNote';
import { dayjs, ET } from 'util/index';
import { screenWidthState, timezoneState } from 'states';
import GALogger, { GALoggerCategory } from 'util/shared/analytics';
import useTooltips from 'hooks/useTooltips';
import { Loader } from 'components';
import localeData from 'dayjs/plugin/localeData';
import useLog from 'hooks/useLog';
import ReportsList from 'components/shared/ReportsList';
import ReportContentWrapper from 'components/shared/ReportContentWrapper';
import DescriptionIcon from '@mui/icons-material/Description';

enum FoundersNoteErrorType {
  Index = 'index',
  SelectedNote = 'selectedNote',
  NoNotes = 'noNotes',
}

enum FoundersNoteLoggerEvents {
  DisplayedNote = 'displayed_note',
  ManuallySelectedNote = 'manually_selected_note',
  NextPage = 'next_page',
  PrevPage = 'prev_page',
  ErrorLoadingNote = 'error_loading_note',
  ErrorLoadingNoteIndex = 'error_loading_note_list',
}

const NotesPerPage = 15;

interface FoundersNotesViewerProps {
  /**
   * Called when a note is selected. Allows the parent (e.g. the page)
   * to update the URL or perform other side-effects.
   */
  onNoteSelect?: (note: FoundersNoteData) => void;
  /** External note ID to load on mount (e.g. from URL params). */
  initialNoteId?: string;
  /** If true, load note via preview key instead of note ID. */
  isPreview?: boolean;
  /** Header slot rendered above the viewer (e.g. admin buttons, print). */
  headerSlot?: React.ReactNode;
  /** Height CSS for the content area. Defaults to 'calc(100vh - 170px)'. */
  contentHeight?: string;
  /** Whether to show the sidebar. Defaults to true. */
  showSidebar?: boolean;
  /** Controlled sidebar open state. Falls back to internal state when omitted. */
  sidebarOpen?: boolean;
  /** Called when sidebar open state changes. */
  onSidebarOpenChange?: (open: boolean) => void;
  /**
   * When true, uses flex-based layout that fills its parent container
   * instead of viewport-relative heights. Eliminates nested scrollbars
   * for embedded contexts like Canvas.
   */
  embedded?: boolean;
  /** Print theme passed from the page for print-friendly rendering. */
  printTheme?: Theme;
  /** Ref callback to expose the note content DOM node for PrintButton. */
  noteContentRef?: React.Ref<HTMLDivElement>;
}

const FoundersNotesViewer = ({
  onNoteSelect,
  initialNoteId,
  isPreview = false,
  headerSlot,
  contentHeight,
  showSidebar = true,
  sidebarOpen: controlledSidebarOpen,
  onSidebarOpenChange,
  embedded = false,
  printTheme,
  noteContentRef,
}: FoundersNotesViewerProps) => {
  dayjs.extend(localeData);

  const [notes, setNotes] = useState<FoundersNotesIdMap | null>(null);
  const [notesLatestPage, setNotesLatestPage] = useState<
    FoundersNoteData[] | null
  >(null);
  const [selectedNote, setSelectedNote] = useState<FoundersNoteData | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [loadingNotesList, setLoadingNotesList] = useState(false);
  const [errored, setErrored] = useState<FoundersNoteErrorType | null>(null);
  const [hasNextPage, setHasNextPage] = useState(true);

  const [page, setPage] = useState(1);
  const isMobile = useIsMobile();
  const screenWidth = useRecoilValue(screenWidthState);
  const isControlled = controlledSidebarOpen != null;
  const [internalOpen, setInternalOpen] = useState(screenWidth >= 1_000);
  const showingDrawer = isControlled ? controlledSidebarOpen : internalOpen;
  const setShowingDrawer = (open: boolean) => {
    if (isControlled) {
      onSidebarOpenChange?.(open);
    } else {
      setInternalOpen(open);
    }
  };
  const [selectedMonth, setSelectedMonth] = useState(dayjs().month());
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [timeFilter, setTimeFilter] = useState<'all' | 'am' | 'pm'>('all');
  const internalRef = useRef<HTMLDivElement>(null);
  const noteRef = noteContentRef ?? internalRef;
  const currentTimezone = useRecoilValue(timezoneState);
  const { logError } = useLog('FoundersNotes');

  const {
    getFoundersNotesIndex,
    getFoundersNoteWithId,
    getFoundersNoteWithPreviewKey,
  } = useFoundersNotes();
  const { loadTooltips } = useTooltips();

  const logger = new GALogger(GALoggerCategory.FoundersNotes);

  const hasInitialId = initialNoteId != null;

  async function fetchIndexData() {
    try {
      if (!loadingNotesList || errored != null) {
        if (selectedNote == null) {
          setLoading(true);
        }
        setLoadingNotesList(true);
        const [response, _] = await Promise.all([
          getFoundersNotesIndex(
            page,
            NotesPerPage,
            selectedMonth,
            selectedYear,
          ),
          loadTooltips(),
        ]);
        if (response.error != null) {
          setErrored(FoundersNoteErrorType.Index);
          return setNotesLatestPage(null);
        }

        const newNotes: FoundersNoteData[] = [];
        const combinedNotes: FoundersNotesIdMap = {};
        if (notes != null) {
          Object.keys(notes!).forEach(
            (key) => (combinedNotes[parseInt(key)] = notes![parseInt(key)]),
          );
        }

        for (let i = 0; i < response.data.length; i++) {
          const newNote = parseNoteJson(response.data[i], currentTimezone);
          newNotes.push(newNote);
          if (combinedNotes[newNote.key] == null) {
            combinedNotes[newNote.key] = newNote;
          }
        }

        setNotesLatestPage(newNotes);
        setHasNextPage(response.hasNextPage);
        setNotes(combinedNotes);
        if (selectedNote == null) {
          setLoading(false);
        }
        if (newNotes.length === 0) {
          setErrored(FoundersNoteErrorType.NoNotes);
        } else {
          if (!hasInitialId && newNotes[0].html && selectedNote == null) {
            const note = newNotes[0];
            setSelectedNote(note);
            onNoteSelect?.(note);
            logger.logEvent(FoundersNoteLoggerEvents.DisplayedNote, {
              label: note.title,
            });
          }
          setErrored(null);
        }
      }
    } catch (err) {
      logError(err, 'fetchIndexData');
      logger.logEvent(FoundersNoteLoggerEvents.ErrorLoadingNoteIndex, {
        label: err?.toString() ?? 'Unknown error',
      });
      setErrored(FoundersNoteErrorType.Index);
      setLoading(false);
    } finally {
      setLoadingNotesList(false);
    }
  }

  useEffect(() => {
    if (hasInitialId) {
      loadSelectedNote(initialNoteId!);
    }
  }, [initialNoteId]);

  useEffect(() => {
    fetchIndexData();
  }, [page, selectedMonth, selectedYear]);

  const loadSelectedNote = async (noteId: string) => {
    if (noteId == null) {
      return;
    }

    const note = (notes ?? {})[noteId];
    onNoteSelect?.(note);

    if (note?.html != null) {
      setSelectedNote(note);
      logger.logEvent(FoundersNoteLoggerEvents.DisplayedNote, {
        label: note.title,
      });
    } else {
      setLoading(true);
      try {
        const [{ data }, _] = await Promise.all([
          isPreview
            ? getFoundersNoteWithPreviewKey(noteId)
            : getFoundersNoteWithId(noteId),
          loadTooltips(),
        ]);

        if (data == null) {
          return setErrored(FoundersNoteErrorType.SelectedNote);
        }
        const newNote = parseNoteJson(data, currentTimezone);
        const combinedNotes: FoundersNotesIdMap = { ...(notes ?? {}) };
        combinedNotes[noteId] = newNote;

        setNotes(combinedNotes);
        setErrored(null);
        setSelectedNote(newNote);
        onNoteSelect?.(newNote);
        logger.logEvent(FoundersNoteLoggerEvents.DisplayedNote, {
          label: data.title ?? '',
        });
      } catch (err) {
        logError(err, 'loadSelectedNote');
        logger.logEvent(FoundersNoteLoggerEvents.ErrorLoadingNote, {
          label: err?.toString() ?? 'Unknown error',
        });
        setSelectedNote(note);
        setErrored(FoundersNoteErrorType.SelectedNote);
      } finally {
        setLoading(false);
      }
    }
  };

  const retryFetch = () => {
    if (errored === FoundersNoteErrorType.Index) {
      fetchIndexData();
    } else if (errored === FoundersNoteErrorType.SelectedNote) {
      loadSelectedNote(selectedNote!.key);
    }

    setErrored(null);
  };

  const refreshSidebar = async () => {
    const month = dayjs().month();
    const year = dayjs().year();
    if (month !== selectedMonth || year !== selectedYear) {
      setSelectedMonth(month);
      setSelectedYear(year);
    } else {
      await fetchIndexData();
    }
  };

  const firstFoundersNoteIndex =
    page === 1
      ? (notesLatestPage ?? []).findIndex((note) =>
          note.title.includes("Founder's Note"),
        )
      : -1;

  const title = selectedNote?.title;
  const loadingOrErrored = loading ? (
    <Loader isLoading={loading} />
  ) : errored ? (
    <a onClick={() => retryFetch()}>
      {errored === FoundersNoteErrorType.NoNotes
        ? 'There are no notes available for this month/year.'
        : 'There was an error fetching this page.'}{' '}
      Click here to retry.
    </a>
  ) : null;

  const toggleShowingDrawer = () => setShowingDrawer(!showingDrawer);

  const nextPage = () => {
    if (!hasNextPage) {
      return;
    }
    const newPage = page + 1;
    logger.logEvent(FoundersNoteLoggerEvents.NextPage, { value: newPage });
    setPage(newPage);
  };

  const previousPage = () => {
    if (page <= 1) {
      return;
    }
    const newPage = page - 1;
    logger.logEvent(FoundersNoteLoggerEvents.PrevPage, { value: newPage });
    setPage(newPage);
    setHasNextPage(true);
  };

  const filteredNotes = (notesLatestPage || []).filter((note) => {
    if (timeFilter === 'all') {
      return true;
    }
    const etHour = note.date.tz(ET).hour();
    return timeFilter === 'am' ? etHour < 12 : etHour >= 12;
  });

  const reportsListProps = {
    items: filteredNotes,
    selectedItemKey: selectedNote?.key ?? null,
    IconComponent: DescriptionIcon,
    onItemSelect: (key: string) => {
      logger.logEvent(FoundersNoteLoggerEvents.ManuallySelectedNote, {
        label: notesLatestPage?.find((note) => note.key === key)?.title ?? '',
      });
      if (isMobile) {
        setShowingDrawer(false);
      }
      loadSelectedNote(key);
    },
    onRefresh: refreshSidebar,
    onClose: toggleShowingDrawer,
    selectedMonth,
    selectedYear,
    onMonthChange: (month: number) => {
      setSelectedMonth(month);
      setPage(1);
    },
    onYearChange: (year: number) => {
      const newYear = year;
      if (newYear === dayjs().year() && selectedMonth > dayjs().month()) {
        setSelectedMonth(dayjs().month());
      }
      setSelectedYear(newYear);
      setPage(1);
    },
    timeFilter,
    onTimeFilterChange: setTimeFilter,
    loading: loadingNotesList,
    showPagination: true,
    currentPage: page,
    hasNextPage,
    onNextPage: nextPage,
    onPreviousPage: previousPage,
    firstNewItemIndex: firstFoundersNoteIndex,
  } as const;

  const notesListComponent = showingDrawer && (
    <ReportsList
      {...reportsListProps}
      sx={embedded ? { height: '100%', width: '100%', minWidth: 0 } : undefined}
    />
  );

  const sidebar = isMobile ? (
    <Drawer
      variant="temporary"
      anchor="right"
      open={showingDrawer}
      onClose={() => setShowingDrawer(false)}
    >
      {notesListComponent}
    </Drawer>
  ) : (
    notesListComponent
  );

  const noteContent = loadingOrErrored ?? (
    <FoundersNote
      html={selectedNote && selectedNote.html ? selectedNote.html : ''}
      title={title}
      fromWordpress={selectedNote?.wordpress ?? false}
      id={selectedNote?.key}
      date={selectedNote?.date}
      printTheme={printTheme}
    />
  );

  const showOpenButton = showSidebar && !showingDrawer;

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: 'column',
        overflow: embedded ? 'hidden' : undefined,
      }}
    >
      {headerSlot}

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          flex: embedded ? 1 : undefined,
          minHeight: embedded ? 0 : undefined,
          height: embedded
            ? undefined
            : { xs: '100%', lg: contentHeight ?? 'calc(100vh - 170px)' },
          flexDirection: 'row',
          overflow: embedded ? 'hidden' : undefined,
        }}
      >
        {embedded ? (
          <Box
            ref={noteRef}
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'auto',
              p: 1,
            }}
          >
            {noteContent}
          </Box>
        ) : (
          <ReportContentWrapper ref={noteRef} theme={printTheme}>
            <Box style={{ overflow: 'auto' }}>{noteContent}</Box>
          </ReportContentWrapper>
        )}
        {showSidebar &&
          (embedded ? (
            showingDrawer ? (
              <Box
                sx={{
                  width: '280px',
                  minWidth: '280px',
                  borderLeft: 1,
                  borderColor: 'divider',
                  overflow: 'hidden',
                  display: 'flex',
                }}
              >
                {sidebar}
              </Box>
            ) : (
              <Tooltip title="Show notes list" placement="left">
                <IconButton
                  onClick={toggleShowingDrawer}
                  color="primary"
                  sx={{
                    alignSelf: 'flex-start',
                    mt: 1,
                    borderLeft: 1,
                    borderColor: 'divider',
                    borderRadius: 0,
                    px: 0.5,
                  }}
                >
                  <ListAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )
          ) : (
            sidebar
          ))}
      </Box>

      {/* Mobile FAB to open notes list */}
      {showOpenButton && isMobile && !embedded && (
        <Fab
          color="primary"
          size="medium"
          onClick={toggleShowingDrawer}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            zIndex: 1_200,
          }}
        >
          <ListAltIcon />
        </Fab>
      )}
    </Box>
  );
};

export default FoundersNotesViewer;
